import { useCallback, useEffect, useRef, useState } from 'react'
import DashboardCard from '../components/DashboardCard'
import DashboardCarousel from '../components/DashboardCarousel'
import {
  getChallengeScheduleForDashboard,
  getChampionResultsForDashboard,
  getExpoNotificationListForDashboard,
} from '../api/guardianExpoService'
import { lectureScheduleDays } from '../data/lectureSchedule'
import { marketScheduleDays } from '../data/marketSchedule'
import { findUpcomingSessions, findTodayChallengeMatches } from '../utils/findCurrentSession'
import { CLASS_BG_COLORS, DEFAULT_CLASS_COLOR } from '../utils/lectureClassColors'

// 資料每分鐘重新拉取一次；只更新資料池，不影響輪播元件目前正在播放的位置
const REFRESH_INTERVAL_MS = 60 * 1000

const STATUS_LABEL = { ongoing: '進行中', upcoming: '即將開始' }
const STATUS_COLOR = { ongoing: '#BC313D', upcoming: '#C59B5A' }
const CHAMPION_MEDAL = {
  總冠軍: '/image/guardian-expo/冠軍.png',
  亞軍: '/image/guardian-expo/亞軍.png',
  季軍: '/image/guardian-expo/季軍.png',
}
const CROWN_ICON = '/image/guardian-expo/正皇冠.png'

// 講座同 classNumber（如 1-1、1-2）合併共用同一個外框，內部各自一行、各自狀態獨立判斷
function groupLectureSessionsByClass(sessions, limit) {
  const order = []
  const groups = new Map()
  for (const session of sessions) {
    if (!groups.has(session.classNumber)) {
      groups.set(session.classNumber, [])
      order.push(session.classNumber)
    }
    groups.get(session.classNumber).push(session)
  }

  return order.slice(0, limit).map((classNumber) => {
    const parts = groups.get(classNumber)
    return { classNumber, class: parts[0].class, parts }
  })
}

export default function GuardianExpoDashboardPage() {
  const [challengeMatches, setChallengeMatches] = useState([])
  const [championResults, setChampionResults] = useState([])
  const [bannerImages, setBannerImages] = useState([])
  // 每分鐘遞增一次，單純用來觸發整頁重新 render、重算「現在時間」相關的進行中／即將開始判斷
  // （講座、市集是寫死的靜態資料，沒有這個 tick 的話畫面會停留在第一次載入當下的時間點不會自動更新）
  const [clockTick, setClockTick] = useState(0)

  useEffect(() => {
    const timer = setInterval(() => setClockTick((t) => t + 1), REFRESH_INTERVAL_MS)
    return () => clearInterval(timer)
  }, [])

  useEffect(() => {
    getChallengeScheduleForDashboard()
      .then((days) => setChallengeMatches(findTodayChallengeMatches(days)))
      .catch((err) => console.error('Failed to fetch challenge schedule for dashboard:', err))
    getChampionResultsForDashboard()
      .then(setChampionResults)
      .catch((err) => console.error('Failed to fetch champion results for dashboard:', err))
  }, [clockTick])

  // 博覽會資訊圖片每捲完一輪（VerticalImageMarquee onCycleComplete）重新取一次最新清單
  // useCallback 保持參考穩定，避免 VerticalImageMarquee 內的計時器因為每次 render 拿到新函式而重建
  const refreshBannerImages = useCallback(() => {
    getExpoNotificationListForDashboard()
      .then(setBannerImages)
      .catch((err) => console.error('Failed to fetch expo notification list for dashboard:', err))
  }, [])

  useEffect(() => {
    refreshBannerImages()
  }, [refreshBannerImages])

  const lectureGroups = groupLectureSessionsByClass(findUpcomingSessions(lectureScheduleDays, Infinity), 4)
  const marketSessions = findUpcomingSessions(marketScheduleDays, 4)

  return (
    <div className="flex flex-col" style={{ height: '100vh', width: '100vw', background: '#FAF7F3', overflow: 'hidden' }}>
      <div className="flex-1 min-h-0 flex flex-col" style={{ padding: 12, gap: 12 }}>
        {/* 講座／技能交換市集 — 7:3 */}
        <div className="flex-1 min-h-0 grid" style={{ gap: 12, gridTemplateColumns: '7fr 3fr' }}>
          <DashboardCard title="講座" color="#BC313D">
            {lectureGroups.length > 0 ? (
              <div className="h-full flex flex-col" style={{ gap: 8 }}>
                {lectureGroups.map((group) => (
                  <LectureRow key={group.classNumber} group={group} />
                ))}
              </div>
            ) : (
              <EmptyState text="敬請期待" />
            )}
          </DashboardCard>

          <DashboardCard title="技能交換市集" color="#C59B5A">
            {marketSessions.length > 0 ? (
              <div className="h-full flex flex-col" style={{ gap: 8 }}>
                {marketSessions.map((session, idx) => (
                  <SessionRow
                    key={idx}
                    status={session.status === 'ongoing' ? 'ongoing' : null}
                    time={session.time}
                    title={session.label}
                  />
                ))}
              </div>
            ) : (
              <EmptyState text="敬請期待" />
            )}
          </DashboardCard>
        </div>

        {/* 挑戰賽／文字看板 — 6:4 */}
        <div className="flex-1 min-h-0 grid" style={{ gap: 12, gridTemplateColumns: '6fr 4fr' }}>
          <DashboardCard title="挑戰賽" color="#004682">
            <div className="h-full flex flex-col" style={{ gap: 8 }}>
              {championResults.length > 0 && <ChampionBoard results={championResults} />}
              <div className="flex-1 min-h-0">
                <DashboardCarousel
                  items={challengeMatches}
                  emptyText="載入中..."
                  renderItem={(entry) => <ChallengeSlide entry={entry} />}
                />
              </div>
            </div>
          </DashboardCard>

          <DashboardCard title="博覽會資訊" color="#226B68" noPadding>
            <VerticalImageMarquee images={bannerImages} onCycleComplete={refreshBannerImages} />
          </DashboardCard>
        </div>
      </div>
    </div>
  )
}

function StatusBadge({ status, small = false, fill = false }) {
  if (!status || !STATUS_LABEL[status]) return null
  return (
    <span
      className={fill ? 'flex items-center justify-center shrink-0' : 'inline-block shrink-0'}
      style={{
        padding: small ? '2px 10px' : '4px 14px',
        borderRadius: 6,
        background: STATUS_COLOR[status],
        fontFamily: 'Noto Sans TC',
        fontSize: small ? 13 : 17,
        fontWeight: 700,
        color: 'white',
        ...(fill ? { alignSelf: 'stretch', margin: '12px 0' } : {}),
      }}
    >
      {STATUS_LABEL[status]}
    </span>
  )
}

// 等速捲動的速度（像素/秒）。不再用固定秒數，避免內容越長的列反而捲得越快；
// 講座、技能交換市集分別可調整各自的速度
const LECTURE_MARQUEE_PX_PER_SEC = 90
const MARKET_MARQUEE_PX_PER_SEC = 60

// 進行中／即將開始時，內容改用跑馬燈橫向捲動來強調；其餘狀態靜態顯示並截斷
// 容器維持 flex-1 撐滿整行寬度（版面不受影響）。量測內容實際寬度，若比容器窄，
// 直接在內容後面補一段等寬空白，讓「一份內容（含補的空白）」的寬度至少跟容器一樣寬。
// 這樣複製兩份、標準無縫位移（0% → -50%）時，同一時刻畫面上最多只會看到一份內容本體，
// 不會提前看到下一份的開頭——內容夠長本身就撐滿容器時，補白為 0，行為等同原本的無縫跑馬燈。
// 動畫時間依「一份內容（含補白）」的實際寬度 ÷ MARQUEE_PX_PER_SEC 動態算出，
// 所以不論內容長短，畫面上捲動的速度（像素/秒）都一致。
function Marquee({ active, pxPerSec = LECTURE_MARQUEE_PX_PER_SEC, children }) {
  const containerRef = useRef(null)
  // 獨立的隱藏量測元素：永遠不會被下面的 key={padWidth} 重新掛載影響，
  // 確保 ResizeObserver 綁定的量測目標一直存在、量到的是純內容寬度（不含補白）
  const measureRef = useRef(null)
  const [padWidth, setPadWidth] = useState(null)
  const [contentWidth, setContentWidth] = useState(0)

  useEffect(() => {
    if (!active) {
      setPadWidth(null)
      return
    }
    const update = () => {
      const containerWidth = containerRef.current?.offsetWidth ?? 0
      const measuredContentWidth = measureRef.current?.offsetWidth ?? 0
      // 尾端間距至少留容器寬度的 10%，避免內容跟下一輪銜接時貼太近
      const minGap = containerWidth * 0.1
      setPadWidth(Math.max(containerWidth - measuredContentWidth, minGap))
      setContentWidth(measuredContentWidth)
    }
    update()
    const observer = new ResizeObserver(update)
    if (containerRef.current) observer.observe(containerRef.current)
    if (measureRef.current) observer.observe(measureRef.current)
    return () => observer.disconnect()
  }, [active])

  if (!active) {
    return (
      <div className="flex-1 min-w-0 flex items-center overflow-hidden" style={{ gap: 14 }}>
        {children}
      </div>
    )
  }

  // 動畫時間 =「一份內容（含補白）」寬度 ÷ 固定速度，讓同一區塊內的捲動速度（像素/秒）一致
  const trackWidth = contentWidth + (padWidth ?? 0)
  const durationSec = padWidth != null ? trackWidth / pxPerSec : 0

  return (
    <div ref={containerRef} className="relative flex-1 min-w-0 overflow-hidden">
      <div ref={measureRef} className="flex items-center shrink-0" style={{ gap: 14, position: 'absolute', visibility: 'hidden', pointerEvents: 'none' }} aria-hidden="true">
        {children}
      </div>
      {/* 尚未量測完成前（padWidth 為 null）不啟動動畫，避免用錯誤的補白（0）先跑一輪、
          量測完成後又因為 key 變動重開而跳動；key={padWidth} 確保補白數值一改變就從頭重跑，
          動畫的百分比基準才會跟當下的實際總寬度一致 */}
      {padWidth != null && (
        <div
          key={padWidth}
          className="marquee-track flex items-center w-max"
          style={{ animationDuration: `${durationSec}s` }}
        >
          <div className="flex items-center shrink-0" style={{ gap: 14, paddingRight: padWidth }}>
            {children}
          </div>
          <div className="flex items-center shrink-0" style={{ gap: 14, paddingRight: padWidth }} aria-hidden="true">
            {children}
          </div>
        </div>
      )}
      <style>{`
        .marquee-track { animation-name: dashboard-marquee; animation-timing-function: linear; animation-iteration-count: infinite; }
        @keyframes dashboard-marquee {
          from { transform: translateX(0); }
          to { transform: translateX(-50%); }
        }
      `}</style>
    </div>
  )
}

function SessionRow({ status, time, title, extra }) {
  const active = status === 'ongoing' || status === 'upcoming'
  return (
    <div
      className="flex-1 min-h-0 flex items-center"
      style={{ gap: 14, padding: '8px 16px', borderRadius: 8, background: 'rgba(188,49,61,0.04)' }}
    >
      <Marquee active={active} pxPerSec={MARKET_MARQUEE_PX_PER_SEC}>
        <StatusBadge status={status} />
        <span className="shrink-0" style={{ fontFamily: 'Noto Serif TC', fontSize: 26, fontWeight: 700, color: '#BC313D' }}>
          {time}
        </span>
        <span className={active ? 'shrink-0' : 'flex-1 min-w-0 line-clamp-1'} style={{ fontFamily: 'Noto Sans TC', fontSize: 24, fontWeight: 700, color: '#171717' }}>
          {title}
        </span>
        {extra && (
          <span className="shrink-0" style={{ fontFamily: 'Noto Sans TC', fontSize: 19, fontWeight: 500, color: '#6E625B' }}>
            {extra}
          </span>
        )}
      </Marquee>
    </div>
  )
}

// 講座卡片：同 classNumber（如 1-1、1-2）合併算成同一組。組內任一段是進行中／即將開始，
// 整組就一起套用跑馬燈；狀態標籤只顯示一個（進行中優先，其次即將開始），垂直置中放在最前面。
// UI 維持原本一段一行（1-1、1-2 各自一行垂直堆疊），但整組包在同一個 Marquee 裡同步橫向捲動，
// 而不是各段各自獨立的跑馬燈。
function LectureRow({ group }) {
  const classColor = CLASS_BG_COLORS[group.classNumber] || DEFAULT_CLASS_COLOR
  const groupStatus = group.parts.some((s) => s.status === 'ongoing')
    ? 'ongoing'
    : group.parts.some((s) => s.status === 'upcoming')
      ? 'upcoming'
      : null
  const active = groupStatus === 'ongoing' || groupStatus === 'upcoming'
  // 非跑馬燈（靜態顯示）時容器寬度固定，可以把講者名稱推到最右邊；
  // 跑馬燈捲動時沒有固定容器右邊界，改成量測每一行「班級＋時間＋標題」的寬度，
  // 取組內最長的一行當基準，其餘較短的行在講者名稱前面補上差額空白，
  // 讓組內每一行的講者名稱都對齊在同一條垂直線上（貼齊最長那行的尾端）
  const leadWidths = useLeadWidths(group.parts.length)
  const speakerWidths = useLeadWidths(group.parts.length)

  return (
    <div
      className="flex items-center min-w-0"
      style={{ flexGrow: group.parts.length, flexBasis: 0, gap: 14, padding: '8px 16px', borderRadius: 8, background: 'rgba(188,49,61,0.04)' }}
    >
      <Marquee active={active}>
        <span className="flex items-center shrink-0" style={{ gap: 14, alignSelf: 'stretch' }}>
          <StatusBadge status={groupStatus} fill />
        </span>
        <div className={active ? 'flex flex-col' : 'flex flex-col flex-1 min-w-0'} style={{ gap: 12 }}>
          {group.parts.map((session, idx) => {
            const speakerStyle = active
              ? {
                  marginLeft: leadWidths.maxWidth != null ? leadWidths.maxWidth - leadWidths.widths[idx] + 32 : 32,
                  width: speakerWidths.maxWidth ?? undefined,
                }
              : { marginLeft: 'auto' }
            return (
              <div key={idx} className={active ? 'flex items-center shrink-0' : 'flex items-center w-full min-w-0'} style={{ gap: 14 }}>
                <span ref={leadWidths.setRef(idx)} className="flex items-center shrink-0" style={{ gap: 14 }}>
                  <span
                    className="flex items-center justify-center shrink-0"
                    style={{ minWidth: 46, padding: '3px 10px', borderRadius: 6, background: classColor, color: 'white', fontFamily: 'Noto Sans TC', fontSize: 21, fontWeight: 700 }}
                  >
                    {session.class}
                  </span>
                  <span className="shrink-0" style={{ fontFamily: 'Noto Serif TC', fontSize: 27, fontWeight: 700, color: '#BC313D' }}>
                    {session.time}
                  </span>
                  <span className={active ? 'shrink-0' : 'min-w-0 line-clamp-1'} style={{ fontFamily: 'Noto Sans TC', fontSize: 25, fontWeight: 700, color: '#171717' }}>
                    {session.title}
                  </span>
                </span>
                {session.speaker && (
                  <span className="shrink-0 flex items-baseline justify-end" style={speakerStyle}>
                    <span ref={speakerWidths.setRef(idx)} className="flex items-baseline shrink-0" style={{ gap: 24 }}>
                      {session.speaker.title && (
                        <span style={{ fontFamily: 'Noto Sans TC', fontSize: 17, fontWeight: 400, color: '#6E625B' }}>
                          {session.speaker.title}
                        </span>
                      )}
                      <span style={{ fontFamily: 'Noto Sans TC', fontSize: 19, fontWeight: 500, color: '#6E625B' }}>
                        {session.speaker.name}
                      </span>
                    </span>
                  </span>
                )}
              </div>
            )
          })}
        </div>
      </Marquee>
    </div>
  )
}

// 量測一組（1-1、1-2…）內每一行「班級＋時間＋標題」的實際寬度，回傳最大值跟各行寬度，
// 供呼叫端計算每一行講者名稱前面該補多少空白，讓講者名稱對齊成一直線
function useLeadWidths(count) {
  const elsRef = useRef([])
  const [widths, setWidths] = useState(() => Array(count).fill(0))

  useEffect(() => {
    const update = () => {
      setWidths(elsRef.current.slice(0, count).map((el) => el?.offsetWidth ?? 0))
    }
    update()
    const observer = new ResizeObserver(update)
    elsRef.current.slice(0, count).forEach((el) => el && observer.observe(el))
    return () => observer.disconnect()
  }, [count])

  const setRef = useCallback((idx) => (el) => { elsRef.current[idx] = el }, [])
  const maxWidth = widths.length > 0 ? Math.max(...widths) : null

  return { widths, maxWidth, setRef }
}

function ChallengeSlide({ entry }) {
  const courts = entry.matchups.filter((m) => m.isCourt)
  const cheerSquads = entry.matchups.filter((m) => !m.isCourt)

  return (
    <div className="h-full flex flex-col justify-center" style={{ gap: 16, padding: '0 8px' }}>
      <div className="flex items-center flex-wrap" style={{ gap: 10, rowGap: 6 }}>
        {entry.category && (
          <span
            className="inline-block shrink-0"
            style={{ padding: '3px 12px', borderRadius: 9999, background: 'rgba(0,70,130,0.1)', fontFamily: 'Noto Sans TC', fontSize: 16, fontWeight: 700, color: '#004682' }}
          >
            {entry.category}
          </span>
        )}
        <span style={{ fontFamily: 'Noto Serif TC', fontSize: 30, fontWeight: 700, color: '#004682' }}>
          {entry.matchTime}
        </span>
        {entry.match != null && (
          <span style={{ fontFamily: 'Noto Sans TC', fontSize: 18, fontWeight: 500, color: '#6E625B' }}>
            第 {entry.match} 場
          </span>
        )}
        <span style={{ fontFamily: 'Noto Sans TC', fontSize: 16, fontWeight: 400, color: '#6E625B' }}>
          到場 {entry.arrivalTime}
        </span>
      </div>

      {/* courts 與 cheerSquads 共用同一個 4 欄 grid（courts 佔 3 欄、cheerSquads 佔 1 欄），
          確保啦啦隊那一欄的寬度跟精舍卡片一致；cheerSquads 欄位永遠佔位（即使空），
          避免右邊消失時左邊 courts 的欄寬跟著重新計算、跑版 */}
      <div className="grid items-stretch" style={{ gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 12 }}>
        <div className="grid" style={{ gridColumn: 'span 3', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 12 }}>
          {courts.map((matchup, idx) => (
            <div
              key={idx}
              className="flex items-center min-w-0"
              style={{ gap: 10, padding: '12px 14px', borderRadius: 8, background: 'rgba(0,70,130,0.04)' }}
            >
              <span
                className="flex items-center justify-center shrink-0"
                style={{ width: 44, height: 44, borderRadius: 8, background: matchup.teamColor, color: 'white', fontFamily: 'Noto Sans TC', fontSize: 22, fontWeight: 700 }}
              >
                {matchup.team}
              </span>
              <span className="flex items-center min-w-0" style={{ gap: 4 }}>
                <span className="line-clamp-1" style={{ fontFamily: 'Noto Sans TC', fontSize: 28, fontWeight: 600, color: '#171717' }}>
                  {matchup.name}
                </span>
                {matchup.isWinner && <img src={CROWN_ICON} alt="優勝" className="shrink-0" style={{ width: 24, height: 24 }} />}
              </span>
            </div>
          ))}
        </div>

        <div className="flex flex-col min-w-0" style={{ gap: 8 }}>
          {cheerSquads.map((matchup, idx) => (
            <div
              key={idx}
              className="flex items-center min-w-0"
              style={{ gap: 10, padding: '12px 14px', borderRadius: 8, background: 'rgba(197,155,90,0.12)' }}
            >
              <span className="flex items-center justify-center shrink-0" style={{ width: 44, height: 44, fontSize: 22 }}>
                🎉
              </span>
              <span className="line-clamp-1" style={{ fontFamily: 'Noto Sans TC', fontSize: 28, fontWeight: 600, color: '#171717' }}>
                {matchup.name}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

const CHAMPION_SLIDE_PX_PER_SEC = 120
const CHAMPION_PAUSE_MS = 600

// 簡易版跑馬燈：跟共用的 Marquee（無縫循環、複製兩份黏在一起連續捲動）不同，
// 這裡 children 是「整條完整內容」（例如總冠軍／亞軍／季軍三組合併），一次全部一起顯示、
// 一起從右滑入、一起跑到完全消失出容器左緣，停頓一下才重播下一輪——不會有下一輪提早出現、疊在一起的情況。
// 用像素量測容器寬度＋內容寬度計算 startX/endX，避免用 % 位移時基準跟容器實際寬度對不上。
function SequentialMarquee({ children }) {
  const [round, setRound] = useState(0)
  const containerRef = useRef(null)
  const contentRef = useRef(null)
  const [metrics, setMetrics] = useState(null)

  useEffect(() => {
    const containerWidth = containerRef.current?.offsetWidth ?? 0
    const contentWidth = contentRef.current?.offsetWidth ?? 0
    setMetrics({ startX: containerWidth, endX: -contentWidth, travel: containerWidth + contentWidth })
  }, [round])

  useEffect(() => {
    if (!metrics) return
    const slideMs = (metrics.travel / CHAMPION_SLIDE_PX_PER_SEC) * 1000
    const timer = setTimeout(() => setRound((r) => r + 1), slideMs + CHAMPION_PAUSE_MS)
    return () => clearTimeout(timer)
  }, [round, metrics])

  const slideMs = metrics ? (metrics.travel / CHAMPION_SLIDE_PX_PER_SEC) * 1000 : 0

  return (
    <div ref={containerRef} className="relative overflow-hidden" style={{ height: 32, width: '100%' }}>
      <div
        key={metrics ? round : 'measure'}
        ref={contentRef}
        className={metrics ? 'sequential-marquee-item absolute flex items-center w-max' : 'absolute flex items-center w-max'}
        style={
          metrics
            ? { animationDuration: `${slideMs}ms`, '--slide-start': `${metrics.startX}px`, '--slide-end': `${metrics.endX}px` }
            : { visibility: 'hidden' }
        }
      >
        {children}
      </div>
      <style>{`
        .sequential-marquee-item { animation-name: dashboard-sequential-marquee; animation-timing-function: linear; animation-fill-mode: forwards; }
        @keyframes dashboard-sequential-marquee {
          from { transform: translateX(var(--slide-start)); }
          to { transform: translateX(var(--slide-end)); }
        }
      `}</style>
    </div>
  )
}

function ChampionBoard({ results }) {
  return (
    <div
      className="flex items-center"
      style={{ padding: '10px 16px', borderRadius: 8, background: 'rgba(197,155,90,0.12)' }}
    >
      <SequentialMarquee>
        <span className="flex items-center shrink-0" style={{ gap: 8 }}>
          <span className="shrink-0" style={{ fontFamily: 'Noto Sans TC', fontSize: 24, fontWeight: 700, color: '#171717' }}>
            🎉狂賀護法技能示範挑戰賽
          </span>
          {results.map(({ category, winner }) => (
            <span key={category} className="flex items-center shrink-0" style={{ gap: 4, marginLeft: 16 }}>
              <img src={CHAMPION_MEDAL[category]} alt={category} className="shrink-0" style={{ width: 24, height: 24 }} />
              <span className="shrink-0" style={{ fontFamily: 'Noto Sans TC', fontSize: 24, fontWeight: 700, color: '#171717' }}>
                {category}
              </span>
              <span className="shrink-0" style={{ fontFamily: 'Noto Sans TC', fontSize: 24, fontWeight: 700, color: '#171717' }}>
                {winner}
              </span>
            </span>
          ))}
        </span>
      </SequentialMarquee>
    </div>
  )
}

function EmptyState({ text }) {
  return (
    <div className="h-full flex items-center justify-center">
      <span style={{ fontFamily: 'Noto Serif TC', fontSize: 26, color: 'rgba(188,49,61,0.4)', letterSpacing: '0.2em' }}>
        {text}
      </span>
    </div>
  )
}

const BANNER_IMAGE_DURATION_SEC = 12

// 圖片持續往下捲動輪播（首尾接龍做無縫循環），每捲完一輪原始清單呼叫 onCycleComplete 取最新資料。
// 圖片寬度吃滿容器（扣除左右內距），高度依原始比例自動撐開（不裁切），所以每張圖片實際高度可能不同，
// 需要實際量測每張圖片渲染後的高度加總，位移距離才會精準，銜接處才不會跳動或留空檔。
function VerticalImageMarquee({ images, onCycleComplete }) {
  const itemRefs = useRef([])
  const [oneSetHeight, setOneSetHeight] = useState(0)
  const cycleDurationSeconds = images.length * BANNER_IMAGE_DURATION_SEC

  useEffect(() => {
    const update = () => {
      const total = itemRefs.current.slice(0, images.length).reduce((sum, el) => sum + (el?.offsetHeight ?? 0), 0)
      setOneSetHeight(total)
    }
    update()
    const observer = new ResizeObserver(update)
    itemRefs.current.slice(0, images.length).forEach((el) => el && observer.observe(el))
    return () => observer.disconnect()
  }, [images])

  useEffect(() => {
    if (!onCycleComplete || images.length === 0) return
    const timer = setInterval(onCycleComplete, cycleDurationSeconds * 1000)
    return () => clearInterval(timer)
  }, [onCycleComplete, cycleDurationSeconds, images.length])

  if (images.length === 0) {
    return <EmptyState text="" />
  }

  return (
    <div className="relative h-full overflow-hidden" style={{ background: 'white', padding: '0 5%' }}>
      <div
        className={oneSetHeight > 0 ? 'vertical-marquee-track flex flex-col' : 'flex flex-col'}
        style={oneSetHeight > 0 ? { animationDuration: `${cycleDurationSeconds}s`, '--marquee-shift': `${oneSetHeight}px` } : undefined}
      >
        {[...images, ...images].map((src, idx) => (
          <div
            key={idx}
            ref={(el) => (itemRefs.current[idx] = el)}
            className="w-full flex flex-col items-center shrink-0"
            style={{ paddingBottom: 56 }}
          >
            <img src={src} alt="博覽會資訊" className="w-full h-auto" style={{ display: 'block' }} />
            <span
              className="shrink-0"
              style={{
                marginTop: 12,
                padding: '2px 10px',
                borderRadius: 6,
                background: 'rgba(0,0,0,0.4)',
                fontFamily: 'Noto Sans TC',
                fontSize: 14,
                fontWeight: 700,
                color: 'white',
              }}
            >
              {(idx % images.length) + 1}/{images.length}
            </span>
          </div>
        ))}
      </div>
      <style>{`
        .vertical-marquee-track { animation-name: dashboard-vertical-marquee; animation-timing-function: linear; animation-iteration-count: infinite; }
        @keyframes dashboard-vertical-marquee {
          from { transform: translateY(0); }
          to { transform: translateY(calc(-1 * var(--marquee-shift))); }
        }
      `}</style>
    </div>
  )
}
